// src/lib/sendEmailFlush.js
//
// Drain-time dispatcher for 'send_email' mutations queued via mutationQueue.
// Invoked from main.jsx's postAction router when the queue drains. This is
// the piece that was missing from Offline Stage 3 — sends used to hang
// synchronously in ComposeEmailModal and never enqueued, so a stuck auth
// lookup or flaky network left the send button spinning forever (2026-04-24
// incident).
//
// Contract:
//   args = {
//     to, cc?, bcc?, subject, html, text?,
//     attachments?: [{ filename, mimeType, data /* base64 */ }],
//     inReplyTo?, threadId?,
//     injectSignature?, sendAsEmail?,
//     linkedDeals?, linkedProjects?, linkedProperties?, linkedOpps?,
//     gmailDraftId?,
//     sendKey?,   // idempotency key — the SAME key on every retry, so a retry
//                 // after a lost response reports the earlier copy instead
//                 // of sending a second one (api/_lib/send-idempotency.js)
//   }
//
// Throws on retriable failure (offline, 5xx, auth-lost) so mutationQueue
// retries with backoff. Throws 4xx errors as permanent so it marks 'failed'.

import { supabase } from './supabase'
import { getSessionWithTimeout } from './authSession'
import { pendingKeyFor } from './composeDraft'

export async function sendEmailFlush(args) {
  // Fetch session at DRAIN time — tokens stored at enqueue time may have
  // expired. Use the resilient helper with a longer timeout since we're
  // already draining in the background; 8s keeps us in the prompt-cache
  // window and survives cold refresh storms.
  const sessionResult = await getSessionWithTimeout(8000)
  if (sessionResult.timedOut) {
    // Throw as transient — will retry on next drain
    throw new Error('HTTP 408 — session lookup timed out')
  }
  const token = sessionResult.data?.session?.access_token
  if (!token) {
    // No session → auth is gone. Mark as permanent failure so the user
    // sees it in the failed bucket and can retry manually after re-login.
    throw new Error('HTTP 401 — not signed in')
  }

  const {
    to, cc, bcc, subject, html, text,
    attachments,
    inReplyTo, threadId,
    injectSignature, sendAsEmail,
    linkedDeals = [],
    linkedProjects = [],
    linkedProperties = [],
    linkedOpps = [],
    gmailDraftId,
    sendKey,
  } = args || {}

  const payload = {
    to, subject, html,
  }
  if (text) payload.text = text
  if (cc) payload.cc = cc
  if (bcc) payload.bcc = bcc
  if (inReplyTo) payload.inReplyTo = inReplyTo
  if (threadId) payload.threadId = threadId
  if (injectSignature === false) payload.injectSignature = false
  if (sendAsEmail) payload.sendAsEmail = sendAsEmail
  if (Array.isArray(attachments) && attachments.length > 0) {
    payload.attachments = attachments
  }
  // A queued send is by definition a possible repeat: the server looks in
  // Sent for this key before sending.
  if (sendKey) { payload.sendKey = sendKey; payload.sendKeyCheck = true }

  const res = await fetch('/api/send-gmail', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`
    try {
      const errData = await res.json()
      if (errData?.error) errMsg = `HTTP ${res.status} — ${errData.error}`
    } catch { /* non-JSON response, fall through */ }
    throw new Error(errMsg)
  }

  const sendResult = await res.json().catch(() => ({}))
  // Confirmed: the compose window's pending copy is no longer needed.
  if (sendKey) { try { localStorage.removeItem(pendingKeyFor(sendKey)) } catch { /* noop */ } }
  const sentMessageId = sendResult?.gmail_message_id || sendResult?.messageId

  // Post-send: link entities + delete the Gmail draft. Both are
  // fire-and-forget on immediate failure — we've already told the user
  // "sent" via the enqueue toast; a failed follow-up shouldn't block.
  const hasLinks = linkedDeals.length || linkedProjects.length || linkedProperties.length || linkedOpps.length
  if (sentMessageId && hasLinks) {
    fetch('/api/inbox-actions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: 'link_entities_after_send',
        gmail_message_id: sentMessageId,
        deal_ids:        linkedDeals,
        project_ids:     linkedProjects,
        property_ids:    linkedProperties,
        opportunity_ids: linkedOpps,
      }),
    }).catch(err => console.warn('[sendEmailFlush] link-entities failed:', err.message))
  }

  if (gmailDraftId) {
    fetch('/api/inbox-actions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'delete_draft', gmail_draft_id: gmailDraftId }),
    }).catch(() => { /* non-fatal */ })
  }

  return { messageId: sentMessageId, threadId: sendResult?.thread_id }
}

// Enqueue a send. Accepts the same shape as sendEmailFlush. Caller is
// responsible for closing the modal / showing a toast; this function just
// persists the payload to the queue.
export async function enqueueSend(args) {
  const { enqueue } = await import('./mutationQueue')
  return enqueue('send_email', args)
}
